import React, { useEffect, useState, useRef } from 'react';
import { Loader2, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AuthCallback = ({ onAuthSuccess, onClose }) => {
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Processing your sign-in...');
  const [error, setError] = useState(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing (React StrictMode)
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      try {
        // Get session_id from URL hash fragment (NOT query params)
        // The format is: /auth/callback#session_id=xxx
        const hash = window.location.hash;
        const urlParams = new URLSearchParams(window.location.search);
        
        let sessionId = null;
        
        // First check hash fragment (primary method)
        if (hash && hash.includes('session_id=')) {
          const hashParams = new URLSearchParams(hash.substring(1)); // Remove the # prefix
          sessionId = hashParams.get('session_id');
        }
        
        // Fallback to query params if no hash
        if (!sessionId) {
          sessionId = urlParams.get('session_id');
        }
        
        if (!sessionId) {
          // Check if there was an error
          const errorParam = urlParams.get('error');
          if (errorParam) {
            throw new Error(decodeURIComponent(errorParam));
          }
          throw new Error('No session ID received from authentication');
        }

        console.log('Processing session_id:', sessionId);
        setMessage('Verifying your Google account...');

        // Call backend to process Google auth
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const response = await fetch(`${API_URL}/api/auth/google`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ session_id: sessionId }),
        });

        let data;
        try {
          data = await response.json();
        } catch (parseError) {
          throw new Error('Invalid response from server');
        }

        if (!response.ok) {
          throw new Error(data.detail || 'Authentication failed');
        }
        
        // Store the token
        localStorage.setItem('auth_token', data.access_token);
        
        // Create user object
        const user = {
          id: data.user.id,
          name: data.user.name,
          email: data.user.email,
          phone: data.user.phone,
          role: data.user.role,
          isAdmin: data.user.is_admin,
          orderCount: data.user.order_count,
        };

        setStatus('success');
        setMessage(data.is_new_user 
          ? `Welcome to Uplands Food Market, ${user.name}!` 
          : `Welcome back, ${user.name}!`
        );

        // Notify parent component
        if (onAuthSuccess) {
          setTimeout(() => {
            onAuthSuccess(user);
            toast.success(data.is_new_user 
              ? 'Account created successfully!' 
              : 'Signed in successfully!'
            );
          }, 1500);
        }

      } catch (err) {
        console.error('Auth callback error:', err);
        setStatus('error');
        setError(err.message || 'Authentication failed. Please try again.');
        setMessage('Authentication failed');
      }
    };

    processCallback();
  }, [onAuthSuccess]);

  const handleRetry = () => {
    // Redirect back to home
    window.location.href = '/';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-orange-50 to-orange-100">
      <Card className="w-full max-w-md mx-4 p-8 text-center shadow-xl">
        {/* Logo */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-orange-600">UPLANDS</h1>
          <p className="text-sm text-gray-500">Food Market</p>
        </div>

        {/* Status Icon */}
        <div className="mb-6">
          {status === 'processing' && (
            <div className="w-16 h-16 mx-auto rounded-full bg-blue-100 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          )}
          {status === 'success' && (
            <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center animate-in zoom-in duration-300">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          )}
          {status === 'error' && (
            <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center animate-in zoom-in duration-300">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
          )}
        </div>

        {/* Message */}
        <h2 className={`text-xl font-semibold mb-2 ${
          status === 'success' ? 'text-green-700' : 
          status === 'error' ? 'text-red-700' : 
          'text-gray-800'
        }`}>
          {status === 'processing' ? 'Signing you in...' :
           status === 'success' ? 'Success!' :
           'Oops!'}
        </h2>
        <p className="text-gray-600 mb-6">{message}</p>

        {/* Error Details */}
        {status === 'error' && error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 text-left">{error}</p>
            </div>
          </div>
        )}

        {/* Actions */}
        {status === 'error' && (
          <div className="space-y-3">
            <Button 
              onClick={handleRetry}
              className="w-full bg-orange-500 hover:bg-orange-600"
            >
              Try Again
            </Button>
            <Button 
              variant="outline"
              onClick={() => window.location.href = '/'}
              className="w-full"
            >
              Return to Home
            </Button>
          </div>
        )}

        {status === 'processing' && (
          <p className="text-sm text-gray-500">
            Please wait while we verify your account...
          </p>
        )}

        {status === 'success' && (
          <p className="text-sm text-gray-500">
            Redirecting you to the store...
          </p>
        )}
      </Card>
    </div>
  );
};

export default AuthCallback;
